import {
  Boxes,
  FolderOpen,
  Server,
  type LucideIcon,
} from "lucide-react";
import amazonS3Logo from "@/assets/storage-logos/amazon-s3.svg";
import boxLogo from "@/assets/storage-logos/box.svg";
import cloudflareLogo from "@/assets/storage-logos/cloudflare.svg";
import dropboxLogo from "@/assets/storage-logos/dropbox.svg";
import googleDriveLogo from "@/assets/storage-logos/google-drive.svg";
import oneDriveLogo from "@/assets/storage-logos/onedrive.svg";
import sakuraInternetLogo from "@/assets/storage-logos/sakura-internet.svg";
import sharePointLogo from "@/assets/storage-logos/sharepoint.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { StorageConnectionKindId } from "@/types/storage-registration";

type StorageLogoSpec =
  | { kind: "asset"; src: string }
  | { kind: "lucide"; icon: LucideIcon };

const CONNECTION_KIND_ICONS: Partial<
  Record<StorageConnectionKindId, StorageLogoSpec>
> = {
  amazon_s3: { kind: "asset", src: amazonS3Logo },
  cloudflare_r2: { kind: "asset", src: cloudflareLogo },
  sakura_object_storage: { kind: "asset", src: sakuraInternetLogo },
  s3_compatible: { kind: "lucide", icon: Server },
  sharepoint: { kind: "asset", src: sharePointLogo },
  google_drive: { kind: "asset", src: googleDriveLogo },
  microsoft_onedrive: { kind: "asset", src: oneDriveLogo },
  box: { kind: "asset", src: boxLogo },
  dropbox: { kind: "asset", src: dropboxLogo },
  smb: { kind: "lucide", icon: Boxes },
  local_folder: { kind: "lucide", icon: FolderOpen },
};

interface StorageConnectionKindIconProps {
  connectionKindId: StorageConnectionKindId;
  className?: string;
}

interface StorageConnectionKindIconBadgeProps
  extends StorageConnectionKindIconProps {
  label: string;
  badgeClassName?: string;
}

export function StorageConnectionKindIcon({
  connectionKindId,
  className = "h-5 w-5",
}: StorageConnectionKindIconProps) {
  const logo = CONNECTION_KIND_ICONS[connectionKindId] ?? {
    kind: "lucide",
    icon: Server,
  };

  if (logo.kind === "asset") {
    return (
      <img
        src={logo.src}
        alt=""
        aria-hidden="true"
        className={`${className} object-contain`}
        data-storage-connection-kind-icon={connectionKindId}
      />
    );
  }

  const Lucide = logo.icon;
  return (
    <Lucide
      className={className}
      aria-hidden="true"
      data-storage-connection-kind-icon={connectionKindId}
    />
  );
}

export function StorageConnectionKindIconBadge({
  connectionKindId,
  label,
  className = "h-4 w-4",
  badgeClassName,
}: StorageConnectionKindIconBadgeProps) {
  const badge = (
    <span
      className={cn(
        "inline-flex h-8 w-8 shrink-0 cursor-help items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-600",
        badgeClassName,
      )}
      aria-label={label}
      tabIndex={0}
    >
      <StorageConnectionKindIcon
        connectionKindId={connectionKindId}
        className={className}
      />
    </span>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
